/**
 * useCategoryManagement Hook
 *
 * Zentrale Verwaltung von Deck-Kategorien (deck_category-Zeilen mit ID) für alle Views.
 * - Initialisiert aus deck.editorSettings.categories (CategoryDTO[])
 * - addCategory legt die Kategorie SOFORT per POST an (liefert die echte ID)
 * - Reihenfolge/excluded werden debounced per PATCH /categories/order gespeichert
 * - Namen sind pro Deck einzigartig (DB-Garantie) — Views dürfen weiter mit Namen arbeiten
 */
import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import { useQueryClient } from '@tanstack/react-query';
import { json } from '../../../api/apiClient';
import { deckCategoriesKey, deckNumericId } from '../utils/deckIds';
import { announceDeckChanged } from './useDeckSyncChannel';

export const CATEGORY_ORDER_DEBOUNCE_MS = 2000;

export function useCategoryManagement({
  deck,
  isOwnProfile = false,
  active = true,
}) {
  const [categories, setCategories] = useState([]);
  const queryClient = useQueryClient();
  // Der Ziel-Wähler des Katalogs liest die Kategorien über React Query; dieser Hook
  // schreibt an React Query vorbei in seinen eigenen State. Ohne die Invalidierung
  // unten stünde dort bis zum Reload ein veralteter Stand — im Zweitfenster auf demselben
  // Deck ebenso, dorthin geht deshalb zusätzlich die Meldung über den Fenster-Abgleich.
  const catalogCategoriesChanged = useCallback(() => {
    const id = deckNumericId(deck);
    if (id == null) return;
    queryClient.invalidateQueries({ queryKey: deckCategoriesKey(id) });
    announceDeckChanged(deck?.id);
  }, [queryClient, deck]);
  const saveTimeoutRef = useRef(null);
  const lastSavedRef = useRef(null);
  const lastDeckIdRef = useRef(null);
  // IDs lokal gelöschter Kategorien: der Deck-Cache (editorSettings.categories)
  // wird beim Löschen NICHT nachgezogen und enthält die Kategorie weiter — ohne
  // diesen Tombstone würde der "server-neue Kategorien anhängen"-Zweig unten sie
  // beim nächsten Sync (z.B. nach dem Anlegen einer anderen Kategorie) fälschlich
  // wieder einfügen (sichtbar bis zum Reload). Serial-IDs kommen nie zurück.
  const removedIdsRef = useRef(new Set());

  // Initialisieren/Synchronisieren aus editorSettings.categories
  useEffect(() => {
    if (!active) return;
    const isNewDeck = deck?.id !== lastDeckIdRef.current;
    if (isNewDeck) {
      lastDeckIdRef.current = deck?.id;
      removedIdsRef.current = new Set(); // neues Deck = frische Serverliste, Tombstone leeren
    }

    const saved = deck?.editorSettings?.categories;
    if (!Array.isArray(saved)) return;

    setCategories((prev) => {
      if (isNewDeck || prev.length === 0) {
        lastSavedRef.current = saved;
        return saved;
      }
      // Server-seitig neu entstandene Kategorien (z.B. durch Auto-Kategorisierung) anhängen —
      // aber nie lokal gelöschte, die nur noch im veralteten Deck-Cache stehen.
      const known = new Set(prev.map((c) => c.id));
      const added = saved.filter((c) => !known.has(c.id) && !removedIdsRef.current.has(c.id));
      if (added.length === 0) return prev;
      return [...prev, ...added];
    });
  }, [active, deck?.editorSettings?.categories, deck?.id]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  // Debounced: Reihenfolge + excluded speichern
  useEffect(() => {
    if (!active || !deck?.id || !isOwnProfile || categories.length === 0) return;
    const payload = categories.map((c) => ({ id: c.id, excluded: !!c.excluded }));
    const savedPayload = (lastSavedRef.current || []).map((c) => ({ id: c.id, excluded: !!c.excluded }));
    if (JSON.stringify(payload) === JSON.stringify(savedPayload)) return;

    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        await json(`/api/decks/${deck.id}/categories/order`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ categories: payload }),
        });
        lastSavedRef.current = categories;
      } catch (error) {
        console.error('Fehler beim Speichern der Kategorie-Reihenfolge:', error);
      }
    }, CATEGORY_ORDER_DEBOUNCE_MS);

    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [active, categories, deck?.id, isOwnProfile]);

  const moveCategory = useCallback((activeName, overName) => {
    if (activeName === overName) return;
    setCategories((prev) => {
      const oldIndex = prev.findIndex((c) => c.name === activeName);
      const newIndex = prev.findIndex((c) => c.name === overName);
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  }, []);

  const moveCategoryByIndex = useCallback((fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    setCategories((prev) => {
      if (fromIndex < 0 || fromIndex >= prev.length) return prev;
      if (toIndex < 0 || toIndex >= prev.length) return prev;
      return arrayMove(prev, fromIndex, toIndex);
    });
  }, []);

  /**
   * Legt die Kategorie sofort serverseitig an (echte ID) und übernimmt sie in den lokalen State.
   * Bei 409 (Name existiert bereits) wird die bestehende Kategorie per Nachlade-GET aufgelöst,
   * per Name gematcht und ebenfalls übernommen. Der Server legt sie zusätzlich in
   * `error.details.category`.
   */
  const addCategory = useCallback(async (categoryName, excluded = false) => {
    if (!categoryName || !deck?.id) return null;
    try {
      const result = await json(`/api/decks/${deck.id}/categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: categoryName, excluded }),
      });
      const category = result?.category;
      if (category) {
        setCategories((prev) =>
          prev.some((c) => c.id === category.id) ? prev : [...prev, category]
        );
        catalogCategoriesChanged();
      }
      return category ?? null;
    } catch (error) {
      if (error?.status === 409) {
        try {
          const normalizedName = categoryName.trim().toLowerCase();
          const { categories: existingCategories } = await json(`/api/decks/${deck.id}/categories`);
          const existing = (existingCategories || []).find(
            (c) => c.name.toLowerCase() === normalizedName
          );
          if (existing) {
            setCategories((prev) =>
              prev.some((c) => c.id === existing.id) ? prev : [...prev, existing]
            );
            catalogCategoriesChanged();
            return existing;
          }
        } catch (reloadError) {
          console.error('Fehler beim Nachladen der bestehenden Kategorie nach 409:', reloadError);
        }
      }
      console.error('Fehler beim Anlegen der Kategorie:', error);
      return null;
    }
  }, [deck?.id, catalogCategoriesChanged]);

  /**
   * Benennt eine Kategorie serverseitig um (PATCH { name }) und zieht den lokalen State nach.
   * 409 (Name existiert) → { ok:false, conflict:true }; der Aufrufer zeigt den Toast.
   * Karten referenzieren Kategorien per NAME — der Deck-Cache wird vom Page-Callback
   * (onCategoryRenamed) nachgezogen, nicht hier.
   */
  const renameCategory = useCallback(async (categoryId, newName) => {
    const name = (newName || '').trim();
    if (!categoryId || !name || !deck?.id) return { ok: false };
    try {
      const result = await json(`/api/decks/${deck.id}/categories/${categoryId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      const category = result?.category;
      if (category) {
        setCategories((prev) => prev.map((c) => (c.id === category.id ? { ...c, name: category.name } : c)));
        catalogCategoriesChanged();
      }
      return { ok: true, category: category ?? null };
    } catch (error) {
      if (error?.status === 409) return { ok: false, conflict: true };
      console.error('Fehler beim Umbenennen der Kategorie:', error);
      return { ok: false };
    }
  }, [deck?.id, catalogCategoriesChanged]);

  /** Entfernt eine Kategorie nur aus dem lokalen State (Server-Löschung macht der DELETE-Endpoint). */
  const removeCategoryLocal = useCallback((categoryName) => {
    if (!categoryName) return;
    setCategories((prev) => {
      // ID(s) der gelöschten Kategorie(n) als Tombstone merken, damit der Sync-Effekt
      // sie nicht aus dem noch nicht nachgezogenen Deck-Cache wieder einfügt.
      prev.filter((c) => c.name === categoryName && c.id != null)
        .forEach((c) => removedIdsRef.current.add(c.id));
      const next = prev.filter((c) => c.name !== categoryName);
      lastSavedRef.current = next; // kein PATCH nötig — Server hat schon gelöscht
      return next;
    });
  }, []);

  const excludedCategoryNames = useMemo(
    () => new Set(categories.filter((c) => c.excluded).map((c) => c.name.toLowerCase())),
    [categories]
  );
  const categoryNames = useMemo(() => categories.map((c) => c.name), [categories]);
  const categoryByName = useMemo(
    () => new Map(categories.map((c) => [c.name.toLowerCase(), c])),
    [categories]
  );
  const commanderCategory = useMemo(
    () => categories.find((c) => c.isCommander) || null,
    [categories]
  );

  return {
    categories,
    categoryOrder: categories, // Alias für Bestands-Konsumenten (gleiches Array, Einträge haben name/excluded)
    categoryNames,
    excludedCategoryNames,
    categoryByName,
    commanderCategory,
    moveCategory,
    moveCategoryByIndex,
    addCategory,
    renameCategory,
    removeCategory: removeCategoryLocal,
    setCategoryOrder: setCategories, // Alias für Bestands-Konsumenten
  };
}

export default useCategoryManagement;
